import React from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { DatadogPrivacyClasses } from "@hopper-b2b/utilities";
import {
  Dialog,
  DialogContent,
  DialogClose,
  PageLoadingScreen,
} from "@tenant/ui";
import styles from "./styles.module.scss";
import { InputField, ActionButton } from "@/components";

export type ResendConfirmationEmailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  form: any;
  getFieldErrorText: (field: string) => string | undefined;
  isLoading: boolean;
  hasError: boolean;
  isSuccess: boolean;
  onSubmit: () => void;
  isFormValid: boolean;
  reset: () => void;
  className?: string;
};

export const ResendConfirmationEmailModal = ({
  isOpen,
  onClose,
  form,
  getFieldErrorText,
  isLoading,
  hasError,
  isSuccess,
  onSubmit,
  isFormValid,
  reset,
  className,
}: ResendConfirmationEmailModalProps) => {
  const [_block, cn] = useModuleBEM(styles, "ResendConfirmationEmail");
  const { t } = useI18nContext();

  const handleClose = () => {
    reset();
    onClose();
  };

  const buttons = (
    <div className={cn("action-buttons")}>
      {isSuccess || hasError ? (
        <ActionButton
          onClick={handleClose}
          message={t("resendConfirmationEmail.modal.done")}
        />
      ) : (
        <>
          <DialogClose>
            <ActionButton
              defaultStyle="outline"
              message={t("resendConfirmationEmail.modal.close")}
            />
          </DialogClose>
          <ActionButton
            disabled={!isFormValid}
            onClick={onSubmit}
            message={t("resendConfirmationEmail.modal.submit")}
          />
        </>
      )}
    </div>
  );

  return (
    <>
      <PageLoadingScreen
        isLoading={isLoading}
        text={t("resendConfirmationEmail.loading")}
        className={cn("loading-screen")}
      />
      <Dialog open={isOpen && !isLoading} onClose={handleClose}>
        <DialogContent
          className={className || cn("modal-container")}
          title={
            isSuccess
              ? t("resendConfirmationEmail.modal.successTitle")
              : t("resendConfirmationEmail.modal.title")
          }
          bottomFixedContent={buttons}
        >
          <div className={cn("modal-content")}>
            {isSuccess ? (
              <div className={cn("success")}>
                {t("resendConfirmationEmail.modal.success")}
              </div>
            ) : hasError ? (
              <div>{t("resendConfirmationEmail.modal.error.description")}</div>
            ) : (
              <div>
                <p className={cn("subtitle")}>
                  {t("resendConfirmationEmail.modal.subtitle")}
                </p>
                <InputField
                  label={t("resendConfirmationEmail.form.labels.email")}
                  className={`${DatadogPrivacyClasses["dd-privacy-mask"]} w-full`}
                  value={form.state.values["email"]}
                  onChangeText={(text) =>
                    form.helpers.onFieldChange("email", text)
                  }
                  errorText={getFieldErrorText("email")}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
